// =============================================================================
// NovoOrcamento — a tela que sustenta o projeto.
//
// A TELA É O DOCUMENTO. Abre já com o orçamento em branco, no formato do
// impresso do Microuniverso, e cada decisão do vendedor é preenchida no lugar
// onde ela aparece no papel. Não existe "formulário do Fênix" de um lado e
// "prévia" do outro: é uma coisa só. No dia em que o ERP cai, ninguém tem tempo
// de aprender tela nova — e não vai precisar, porque reconhece o papel.
//
// Duas ideias continuam guiando o desenho:
//
// 1. TIPO DE ENTREGA É POR ITEM. Um orçamento pode ter torneira que o cliente
//    leva agora e piso entregue em três semanas. Por isso o tipo de saída é uma
//    coluna da tabela, como no ERP — não um seletor único no rodapé.
//
// 2. NADA SE PERDE. O vendedor passa uma hora com o cliente escolhendo
//    material; fechar a aba sem querer não pode custar isso. São DUAS camadas,
//    porque elas falham de jeitos diferentes:
//
//      · localStorage — instantâneo, e funciona mesmo sem rede. Cobre o susto:
//        fechou a aba, reabriu, está tudo lá.
//      · rascunho no servidor — sobrevive ao computador. O orçamento aparece
//        na lista e pode ser retomado de outra máquina.
//
//    Sozinha, cada uma tem um buraco: o localStorage some com o navegador, e o
//    servidor não recebe nada se a rede cair no meio. Juntas, não.
//
// 3. O VENDEDOR PRECISA SABER O QUE ESTÁ PROMETENDO. Reserva não existe fora do
//    ERP: enquanto ele está fora, o estoque é uma fotografia. A idade dessa
//    fotografia fica visível o tempo todo, e nunca escondemos "faltam 3".
// =============================================================================

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Swal from 'sweetalert2';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api, { mensagemDeErro, setCracha, crachaDevLembrado } from '../services/api';
import IdentificaVendedor from '../components/IdentificaVendedor';
import { useAuth } from '../contexts/AuthContext';
import DocumentoOrcamento from '../components/DocumentoOrcamento';

const dinheiro = (v) =>
  Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

// ---------------------------------------------------------------------------
// Faixa de estado: de onde vem o saldo. Fica sempre no topo, acima do
// documento — é contexto de quem vende, não conteúdo do papel do cliente.
// O saldo é lido do Microuniverso a cada consulta: não há idade a mostrar.
// (Até 29/10/2026 aqui aparecia a idade do espelho da versão VPS, aposentada.)
// ---------------------------------------------------------------------------
function FaixaEstado({ estoque }) {
  if (!estoque) return null;
  return (
    <div className="faixa-estado faixa-ok">
      <i className="bi bi-broadcast" />
      <span>Estoque e preço <strong>ao vivo</strong>, direto do Microuniverso.</span>
    </div>
  );
}

// A tela de Rascunhos lê a mesma chave: o rascunho que ainda não subiu para o
// servidor existe só aqui, e sonegá-lo da lista seria mentir sobre o que o
// vendedor tem em mãos.
// 'AAAA-MM-DD' no fuso do Brasil. toISOString() é UTC: depois das 21h já seria amanhã.
const dataLocal = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

export const CHAVE_LOCAL = 'fnx:orcamento-em-digitacao';

// ---------------------------------------------------------------------------
// Tela
// ---------------------------------------------------------------------------
export default function NovoOrcamento() {
  const { usuario, temPermissao } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  // Quem está vendendo. Sem isso não se monta orçamento: a senha do vendedor é
  // pedida ao INICIAR o pedido, porque é ela que diz de quem é a venda.
  const [vendedor, setVendedor] = useState(null);
  const [apoio, setApoio] = useState(null);
  const [filial, setFilial] = useState(usuario?.filial_padrao || '07');
  const [itens, setItens] = useState([]);
  const [cliente, setCliente] = useState(null);
  const [plano, setPlano] = useState('');
  const [tpo, setTpo] = useState('');
  const [tipoDocumento, setTipoDocumento] = useState('');
  const [descontoCabecalho, setDescontoCabecalho] = useState(0);
  const [observacao, setObservacao] = useState('');
  const [estoque, setEstoque] = useState(null);
  const [simulacao, setSimulacao] = useState(null);
  const [simulando, setSimulando] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const timerSim = useRef(null);

  // O uuid nasce com o orçamento e não muda: é ele que faz salvar cem vezes
  // gerar UM orçamento, e é ele que o `POST /` usa para confirmar no lugar.
  const [uuid, setUuid] = useState(() => crypto.randomUUID());
  const [rascunho, setRascunho] = useState({ estado: 'limpo', em: null });
  const timerRascunho = useRef(null);
  const primeiroRender = useRef(true);

  useEffect(() => {
    api.get('/api/catalogo/apoio')
      .then((r) => setApoio(r.data))
      .catch(() => {});
  }, []);

  // ⚠️ A forma de pagamento não tem mais padrão fixo no código. Ela era 'AVISTA'
  //    até 25/08/2026, quando a lista deixou de ser semente e passou a vir do
  //    ERP com o OID como código — 'AVISTA' virou um valor que não existe na
  //    lista, e `<select>` com valor fora da lista MENTE: mostra a primeira
  //    opção como se estivesse escolhida. O padrão agora é a forma mais usada,
  //    que é a primeira que o backend devolve (A VISTA, 52% da venda).
  useEffect(() => {
    if (!apoio?.planos?.length) return;
    setPlano((atual) => (apoio.planos.some((p) => p.codigo === atual) ? atual : apoio.planos[0].codigo));
  }, [apoio]);

  // O tipo de pedido é escolha do vendedor — ele já faz isso hoje no ERP. O
  // Fênix só sugere o mais usado NA FILIAL (varia: umas lojas usam VENDAS DE
  // MERCADORIAS, outras VENDAS PROJETADO) e deixa trocar a qualquer momento.
  useEffect(() => {
    if (!apoio?.filiais?.length) return;
    const f = apoio.filiais.find((x) => x.filial === filial);
    if (f?.tpo_padrao) setTpo(f.tpo_padrao);
  }, [apoio, filial]);

  const simular = useCallback(async () => {
    if (!itens.length) { setSimulacao(null); return; }
    setSimulando(true);
    try {
      const { data } = await api.post('/api/pedidos/simular', {
        filial_venda: filial,
        codclie: cliente?.codclie,
        cliente_rascunho_id: cliente?.id && !cliente?.codclie ? cliente.id : undefined,
        plano_pagamento: plano,
        desconto_cabecalho: Number(descontoCabecalho) || 0,
        itens: itens.map((i) => ({
          codpro: i.codpro,
          quantidade: Number(i.quantidade) || 0,
          preco_unitario: Number(i.preco_unitario) || 0,
          desconto_unitario: Number(i.desconto_unitario) || 0,
          tipo_entrega: i.tipo_entrega || 'C',
          filial_retirada: i.filial_retirada || null,
          faturamento_direto: i.faturamento_direto || 0,
          dt_prev_ent: i.dt_prev_ent || null,
          sem_saldo_confirmado: !!i.sem_saldo_confirmado,
          observacao: i.observacao || null,
        })),
      });
      setSimulacao(data);
    } catch (e) {
      setSimulacao({ erros: [mensagemDeErro(e)], pode_confirmar: false, itens: [], totais: {} });
    } finally {
      setSimulando(false);
    }
  }, [itens, filial, cliente, plano, descontoCabecalho]);

  useEffect(() => {
    if (timerSim.current) clearTimeout(timerSim.current);
    timerSim.current = setTimeout(simular, 260);
    return () => timerSim.current && clearTimeout(timerSim.current);
  }, [simular]);

  // A previsão padrão é HOJE: o spike S-3 mediu que 97,4% das vendas 'C',
  // 97,9% das 'E' e 77,5% das 'F' têm entrega no mesmo dia do pedido. Pedir uma
  // data futura por padrão seria atrito contra o fluxo real da loja.
  const hoje = dataLocal();

  const adicionarProduto = useCallback(async (p) => {
    // Busca a ficha para já trazer as opções de atendimento (a árvore mastigada).
    let opcoes = [];
    try {
      // Pede as opções de Expedição: elas trazem a loja da venda (se tiver saldo)
      // e as outras filiais. Caixa só sai daqui.
      const { data } = await api.get(`/api/catalogo/produtos/${p.codpro}`, {
        params: { filial, quantidade: Number(p.venda_minima) || 1, tipo_entrega: 'E' },
      });
      opcoes = data.opcoes_atendimento || [];
    } catch { /* sem opções, o padrão é a própria filial */ }

    // Tem na loja: Caixa, daqui. Só em outra loja: já entra como Expedição, saindo
    // da filial com mais saldo — Caixa de outra loja o confirmar recusaria, e o
    // vendedor só descobriria no fim. Sem saldo em lugar nenhum: Caixa, e a linha
    // mostra que falta saldo.
    const daLoja = opcoes.find((o) => o.caso === 'A');
    const deFora = !daLoja && opcoes.find((o) => o.caso === 'B1');
    const recomendada = daLoja || deFora || null;
    const tipoInicial = deFora ? 'E' : 'C';
    setItens((atual) => [...atual, {
      chave: crypto.randomUUID(),
      codpro: p.codpro,
      descricao: p.descricao,
      unidade: p.unidade,
      foto: p.foto,
      venda_minima: p.venda_minima,
      quantidade: Number(p.venda_minima) || 1,
      preco_unitario: Number(p.preco_venda),
      desconto_unitario: 0,
      tipo_entrega: tipoInicial,
      dt_prev_ent: hoje,
      filial_retirada: recomendada?.filial_retirada || null,
      faturamento_direto: recomendada?.faturamento_direto || 0,
      opcoes_atendimento: opcoes,
    }]);
  }, [filial, hoje]);

  const alterarItem = useCallback((chave, mudancas) => {
    setItens((atual) => atual.map((i) => (i.chave === chave ? { ...i, ...mudancas } : i)));
  }, []);

  const removerItem = useCallback((chave) => {
    setItens((atual) => atual.filter((i) => i.chave !== chave));
  }, []);

  // Tudo que compõe o orçamento, num objeto só. Serve para gravar o rascunho,
  // para o espelho no localStorage e para comparar se algo mudou de verdade.
  const conteudo = useMemo(() => ({
    uuid, filial_venda: filial, codvend: vendedor?.codvend || null,
    codclie: cliente?.codclie || null,
    cliente_rascunho_id: cliente?.id && !cliente?.codclie ? cliente.id : null,
    cliente: cliente
      ? { razao: cliente.razao, cpf_cnpj: cliente.cpf_cnpj, endereco: cliente.endereco,
          bairro: cliente.bairro, cidade: cliente.cidade, uf: cliente.uf, cep: cliente.cep }
      : null,
    plano_pagamento: plano, tipo_documento: tipoDocumento || null, tpo,
    desconto_cabecalho: Number(descontoCabecalho) || 0,
    observacao,
    itens,
  }), [uuid, filial, vendedor, cliente, plano, tipoDocumento, tpo, descontoCabecalho, observacao, itens]);

  const temConteudo = itens.length > 0 || !!cliente;

  // -------------------------------------------------------------------------
  // Camada 1: localStorage. Instantâneo e sem rede. É o que cobre o susto de
  // fechar a aba — quando o vendedor reabre, está tudo lá antes mesmo de a API
  // responder.
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (!temConteudo) return;
    try {
      localStorage.setItem(CHAVE_LOCAL, JSON.stringify({
        ...conteudo,
        cliente_completo: cliente,   // o objeto inteiro, para remontar a tela
        em: new Date().toISOString(),
      }));
    } catch { /* cota cheia ou modo privado: a camada do servidor continua */ }
  }, [conteudo, cliente, temConteudo]);

  // -------------------------------------------------------------------------
  // Camada 2: rascunho no servidor. Sobrevive ao computador e aparece na lista.
  // Salva 2,5s depois da última tecla — tempo de o vendedor terminar de digitar
  // sem transformar cada caractere numa requisição.
  // -------------------------------------------------------------------------
  const salvarRascunho = useCallback(async (corpo) => {
    setRascunho((r) => ({ ...r, estado: 'salvando' }));
    try {
      const { data } = await api.put(`/api/pedidos/rascunho/${corpo.uuid}`, corpo);
      setRascunho({ estado: 'salvo', em: data.salvo_em, numero: data.numero });
    } catch (e) {
      // Falhar aqui NÃO é perder: o localStorage já guardou. A tela diz que a
      // cópia do servidor está atrasada, sem alarmar quem está vendendo.
      setRascunho((r) => ({ ...r, estado: 'offline' }));
    }
  }, []);

  useEffect(() => {
    // Não gravar rascunho vazio: senão abrir a tela e sair já cria orçamento.
    if (primeiroRender.current) { primeiroRender.current = false; return; }
    if (!vendedor || !temConteudo) return;
    if (timerRascunho.current) clearTimeout(timerRascunho.current);
    setRascunho((r) => (r.estado === 'salvo' ? { ...r, estado: 'pendente' } : r));
    timerRascunho.current = setTimeout(() => salvarRascunho(conteudo), 2500);
    return () => timerRascunho.current && clearTimeout(timerRascunho.current);
  }, [conteudo, vendedor, temConteudo, salvarRascunho]);

  // O documento é alimentado por UM objeto no formato do pedido salvo. Assim o
  // orçamento em digitação e o orçamento impresso são a mesma coisa, e não duas
  // telas parecidas que divergem na primeira mudança.
  const documento = useMemo(() => ({
    numero: null,
    numped_erp: null,
    criado_em: hoje,
    filial_venda: filial,
    filial_nome: apoio?.filiais?.find((f) => f.filial === filial)?.nome || '',
    vendedor_nome: vendedor?.nome || '',
    criado_por_nome: usuario?.nome || '',
    codclie: cliente?.codclie || null,
    cliente_razao: cliente?.razao || '',
    cliente_cpf_cnpj: cliente?.cpf_cnpj || '',
    cliente_endereco: cliente?.endereco || '',
    cliente_bairro: cliente?.bairro || '',
    cliente_cidade: cliente?.cidade || '',
    cliente_uf: cliente?.uf || '',
    cliente_cep: cliente?.cep || '',
    cliente_pendencia: cliente?.cliente_pendencia || cliente?.pendencia || null,
    plano_pagamento: plano,
    tipo_documento: tipoDocumento,
    tpo,
    observacao,
    valor_bruto: simulacao?.totais?.valor_bruto || 0,
    desconto_cabecalho: simulacao?.totais?.desconto_cabecalho || 0,
    valor_liquido: simulacao?.totais?.valor_liquido || 0,
  }), [hoje, filial, apoio, vendedor, usuario, cliente, plano, tipoDocumento, tpo, observacao, simulacao]);

  // Cada linha carrega o que veio da digitação E o que voltou da simulação —
  // inclusive erros e avisos, que aparecem na própria linha que os causou.
  const itensDocumento = useMemo(() => itens.map((i, indice) => {
    const bruto = simulacao?.itens?.[indice] || {};
    // A simulação desta linha só vale se foi feita com o item como ele está
    // agora. Antes de a resposta nova chegar, a explicação descreveria a opção
    // anterior ("Cliente retira na filial 06" numa Futura).
    const e = bruto.entrada;
    const emDia = !e || (e.tipo_entrega === (i.tipo_entrega || 'C')
      && (e.filial_retirada || null) === (i.filial_retirada || null)
      && e.faturamento_direto === (i.faturamento_direto ? 1 : 0)
      && Number(e.quantidade) === Number(i.quantidade));
    const calc = emDia ? bruto : { valor_liquido: bruto.valor_liquido, opcoes_atendimento: bruto.opcoes_atendimento };
    const d = calc.derivados || null;
    return {
      ...i,
      // Rascunho antigo pode ter guardado só o código: o nome vem da simulação.
      descricao: (i.descricao && i.descricao !== i.codpro) ? i.descricao : (bruto.descricao || i.descricao),
      unidade: i.unidade || bruto.unidade || null,
      sequencia: indice + 1,
      valor_liquido: calc.valor_liquido || 0,
      filial_estoque: d?.filial_estoque || null,
      aguarda_transferencia: d?.aguarda_transferencia ? 1 : 0,
      sem_saldo_confirmado: i.sem_saldo_confirmado ? 1 : 0,
      erros: calc.erros || [],
      avisos: calc.avisos || [],
      derivados: d,
      // ⚠️ As opções vêm da SIMULAÇÃO, não do que foi buscado ao adicionar o
      //    item. Elas dependem da quantidade, da filial e do tipo de saída —
      //    tudo isso muda enquanto o vendedor digita, e a lista buscada uma vez
      //    só envelhece em silêncio. De quebra, rascunho retomado volta com as
      //    opções sem precisar de consulta extra.
      opcoes_atendimento: calc.opcoes_atendimento || i.opcoes_atendimento || [],
    };
  }), [itens, simulacao]);

  // -------------------------------------------------------------------------
  // Retomada. Um orçamento só é abandonado quando o vendedor DIZ que é.
  //
  // A oferta acontece depois da senha do vendedor, e não antes: é ali que se
  // sabe de quem é a venda. O localStorage entra primeiro porque é mais novo
  // que o servidor por até 2,5s — e são justamente os últimos segundos, os que
  // o vendedor lembra de ter digitado.
  // -------------------------------------------------------------------------
  const restaurar = useCallback((d) => {
    setUuid(d.uuid || crypto.randomUUID());
    if (d.filial_venda) setFilial(d.filial_venda);
    // Previsão que venceu enquanto o rascunho esperava não é promessa: volta em
    // branco e o item pede uma data nova — o vendedor decide, não a tela.
    const hojeLocal = dataLocal();
    setItens((d.itens || []).map((i) => (i.dt_prev_ent && String(i.dt_prev_ent).slice(0, 10) < hojeLocal
      ? { ...i, dt_prev_ent: null } : i)));
    setCliente(d.cliente_completo || null);
    // O cliente guardado no rascunho é um retrato de quando ele foi escolhido.
    // O confirmar manda o cliente como o ERP tem AGORA (o endereço mais novo):
    // o papel tem de mostrar o mesmo, senão o documento diz uma rua e a entrega vai a outra.
    if (d.cliente_completo?.codclie) {
      api.get(`/api/clientes/${d.cliente_completo.codclie}`)
        .then(({ data }) => { if (data?.cliente) setCliente(data.cliente); })
        .catch(() => { /* fica o retrato; a busca de cliente continua disponível */ });
    }
    setPlano(d.plano_pagamento || '');
    setTipoDocumento(d.tipo_documento || '');
    if (d.tpo) setTpo(d.tpo);
    setDescontoCabecalho(d.desconto_cabecalho || 0);
    setObservacao(d.observacao || '');
    setRascunho({ estado: 'salvo', em: d.em || null, numero: d.numero });
  }, []);

  // Traz um rascunho do servidor e remonta a tela com ele. Usado pelo aviso de
  // "continuar de onde parou?" e pela tela de Rascunhos, que manda o uuid na
  // URL — o mesmo caminho, para as duas não divergirem.
  const carregarDoServidor = useCallback(async (uuidAlvo, vendedorAtual) => {
    try {
      const { data } = await api.get(`/api/pedidos/${uuidAlvo}`);
      // ⚠️ Dois vendedores no mesmo balcão não herdam o orçamento um do outro.
      //    Vale principalmente para a tela de Rascunhos, que manda o uuid pela
      //    URL: o rascunho é do usuário, mas a venda é de um vendedor.
      if (vendedorAtual && data.pedido.codvend && data.pedido.codvend !== vendedorAtual.codvend) {
        Swal.fire({
          icon: 'info',
          title: 'Esse rascunho é de outro vendedor',
          text: `Ele foi começado por ${data.pedido.vendedor_nome || data.pedido.codvend}. `
              + 'Entre com a senha dele para continuar.',
          confirmButtonColor: '#EA6A08',
        });
        return;
      }
      restaurar({
        uuid: data.pedido.uuid,
        numero: data.pedido.numero,
        filial_venda: data.pedido.filial_venda,
        plano_pagamento: data.pedido.plano_pagamento,
        tipo_documento: data.pedido.tipo_documento,
        tpo: data.pedido.tpo,
        desconto_cabecalho: Number(data.pedido.desconto_cabecalho) || 0,
        observacao: data.pedido.observacao || '',
        cliente_completo: data.pedido.codclie
          ? { codclie: data.pedido.codclie, razao: data.pedido.cliente_razao,
              cpf_cnpj: data.pedido.cliente_cpf_cnpj, endereco: data.pedido.cliente_endereco,
              bairro: data.pedido.cliente_bairro, cidade: data.pedido.cliente_cidade,
              uf: data.pedido.cliente_uf, cep: data.pedido.cliente_cep }
          : null,
        itens: (data.itens || []).map((i) => ({
          chave: crypto.randomUUID(),
          codpro: i.codpro, descricao: i.descricao, unidade: i.unidade, foto: i.foto,
          quantidade: Number(i.quantidade), preco_unitario: Number(i.preco_unitario),
          desconto_unitario: Number(i.desconto_unitario) || 0,
          tipo_entrega: i.tipo_entrega, filial_retirada: i.filial_retirada,
          // BIT chega como true/false; a tela compara com 1/0.
          faturamento_direto: i.faturamento_direto ? 1 : 0, dt_prev_ent: i.dt_prev_ent,
          sem_saldo_confirmado: !!i.sem_saldo_confirmado, observacao: i.observacao,
          // As opções de atendimento vêm do catálogo, não do pedido: o painel
          // do item as recarrega quando o vendedor abrir.
          opcoes_atendimento: [],
        })),
      });
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Não deu para abrir o rascunho', text: mensagemDeErro(e) });
    }
  }, [restaurar]);

  const oferecerRetomada = useCallback(async (vendedorAtual) => {
    let local = null;
    try {
      const cru = localStorage.getItem(CHAVE_LOCAL);
      if (cru) local = JSON.parse(cru);
    } catch { /* json quebrado nao pode travar a tela */ }

    // Só oferece o que é do MESMO vendedor: dois vendedores no mesmo balcão não
    // podem herdar o orçamento um do outro.
    const localVale = local && (!local.codvend || local.codvend === vendedorAtual.codvend)
      && ((local.itens || []).length > 0 || local.cliente_completo);

    let doServidor = [];
    try {
      const { data } = await api.get('/api/pedidos/rascunhos');
      doServidor = (data.rascunhos || []).filter(
        (r) => !r.codvend || r.codvend === vendedorAtual.codvend
      );
    } catch { /* sem rede: o local ja resolve */ }

    if (!localVale && !doServidor.length) return;

    const candidato = localVale ? local : null;
    const lista = doServidor.filter((r) => r.uuid !== candidato?.uuid);

    const linhas = [
      candidato && `<div class="mb-2"><strong>Neste computador</strong><br/>`
        + `<span class="text-muted">${(candidato.itens || []).length} item(ns)`
        + `${candidato.cliente_completo ? ' · ' + candidato.cliente_completo.razao : ''}</span></div>`,
      ...lista.slice(0, 3).map((r) => `<div class="mb-1"><strong>${r.numero}</strong> — `
        + `${r.itens} item(ns)${r.cliente_razao ? ' · ' + r.cliente_razao : ''}</div>`),
    ].filter(Boolean).join('');

    const r = await Swal.fire({
      icon: 'question',
      title: 'Continuar de onde parou?',
      html: `Há orçamento em digitação que não foi confirmado:<br/><br/>${linhas}`,
      showCancelButton: true,
      confirmButtonText: 'Continuar',
      cancelButtonText: 'Começar em branco',
      confirmButtonColor: '#EA6A08',
    });
    if (!r.isConfirmed) return;

    if (candidato) { restaurar(candidato); return; }

    carregarDoServidor(lista[0].uuid);
  }, [restaurar, carregarDoServidor]);


  // Retomada dirigida pela tela de Rascunhos: /orcamento?retomar=<uuid|local>.
  // Aqui não se pergunta nada — a escolha já foi feita lá, e repetir a pergunta
  // seria desfazer o clique do vendedor.
  const retomarAlvo = useCallback(async (alvo, vendedorAtual) => {
    // Limpa o parâmetro: com ele na URL, um F5 no meio da digitação recarregaria
    // o rascunho por cima do que estivesse na tela.
    navigate('/orcamento', { replace: true });

    if (alvo !== 'local') { carregarDoServidor(alvo, vendedorAtual); return; }

    let local = null;
    try {
      const cru = localStorage.getItem(CHAVE_LOCAL);
      if (cru) local = JSON.parse(cru);
    } catch { /* json quebrado nao pode travar a tela */ }

    if (!local || ((local.itens || []).length === 0 && !local.cliente_completo)) {
      Swal.fire({
        icon: 'info', title: 'Esse rascunho não está mais aqui',
        text: 'Ele existia só neste computador e foi apagado ou confirmado.',
        confirmButtonColor: '#EA6A08',
      });
      return;
    }
    if (local.codvend && local.codvend !== vendedorAtual.codvend) {
      Swal.fire({
        icon: 'info', title: 'Esse rascunho é de outro vendedor',
        text: 'Entre com a senha dele para continuar.',
        confirmButtonColor: '#EA6A08',
      });
      return;
    }
    restaurar(local);
  }, [navigate, carregarDoServidor, restaurar]);

  // -------------------------------------------------------------------------
  // Cadastro de cliente sem sair da venda — vai ao Microuniverso NA HORA, pela
  // API de venda. Cliente novo é cadastrado; quem já é cliente só ganha
  // endereço novo (é o que a API permite: ver routes/clientes.js).
  // -------------------------------------------------------------------------
  const [ficha, setFicha] = useState(null);   // null | { cliente, salvando, erro }

  const salvarCliente = useCallback(async (dados) => {
    const codclie = ficha?.cliente?.codclie;
    setFicha((f) => ({ ...f, salvando: true, erro: '' }));
    try {
      const { data } = codclie
        ? await api.post(`/api/clientes/${codclie}/endereco`, dados)
        : await api.post('/api/clientes', dados);
      // O cliente do orçamento passa a ser o que o Microuniverso tem agora —
      // com o endereço novo, que é o que o documento mostra e o que vai no envio.
      setCliente(data.cliente);
      setFicha(null);
      if (data.aviso) {
        Swal.fire({ icon: 'info', title: 'Pronto', text: data.aviso, confirmButtonColor: '#EA6A08' });
      }
    } catch (e) {
      const resp = e.response?.data;
      // 409 com cadastro existente: em vez de mandar procurar, coloca no orçamento.
      if (resp?.cliente) {
        setCliente(resp.cliente);
        setFicha(null);
        Swal.fire({
          icon: 'info', title: 'Esse cliente já existe',
          text: `${resp.erro} Ele já foi colocado no orçamento.`,
          confirmButtonColor: '#EA6A08',
        });
        return;
      }
      setFicha((f) => ({ ...f, salvando: false, erro: mensagemDeErro(e) }));
    }
  }, [ficha]);

  // O CPF/CNPJ é a primeira pergunta da ficha: se ele revela um cliente que já
  // existe, o cliente vai direto para o orçamento — não há o que cadastrar.
  const clienteEncontrado = useCallback((achado) => {
    setCliente(achado);
    setFicha(null);
    Swal.fire({
      icon: 'info',
      title: 'Esse cliente já está cadastrado',
      text: `${achado.razao} foi colocado no orçamento.`,
      confirmButtonColor: '#EA6A08',
    });
  }, []);

  // ⚠️ `setEstoque` e os `set*` do useState são passados direto de propósito:
  //    a busca de produto tem `aoMedirEstoque` nas dependências do efeito, e uma
  //    função recriada a cada render faria a busca disparar em loop.
  const edicao = {
    apoio, filial, hoje,
    onFilial: setFilial,
    onTrocarVendedor: () => { setCracha(null); setVendedor(null); },
    onCliente: setCliente,
    onItem: alterarItem,
    onRemoverItem: removerItem,
    onAdicionarProduto: adicionarProduto,
    descontoCabecalho, onDesconto: setDescontoCabecalho,
    plano, onPlano: setPlano,
    tipoDocumento, onTipoDocumento: setTipoDocumento,
    tpo, onTpo: setTpo,
    observacao, onObservacao: setObservacao,
    aoMedirEstoque: setEstoque,
    podeEditarCliente: temPermissao('clientes_criar'),
    onNovoCliente: () => setFicha({ cliente: null, salvando: false, erro: '' }),
    onCorrigirCliente: () => setFicha({ cliente, salvando: false, erro: '' }),
    ficha: ficha && {
      ...ficha, aoSalvar: salvarCliente, aoEncontrar: clienteEncontrado,
      aoCancelar: () => setFicha(null),
    },
  };

  // Descartar é decisão explícita, e por isso pergunta. Sem SweetAlert aqui o
  // vendedor perderia uma hora de trabalho num clique errado.
  async function descartarRascunho() {
    const r = await Swal.fire({
      icon: 'warning',
      title: 'Descartar este orçamento?',
      html: `São <strong>${itens.length} item(ns)</strong>`
          + `${cliente ? ` para <strong>${cliente.razao}</strong>` : ''}. Isso não tem volta.`,
      showCancelButton: true,
      confirmButtonText: 'Descartar',
      cancelButtonText: 'Continuar o orçamento',
      confirmButtonColor: '#b91c1c',
    });
    if (!r.isConfirmed) return;

    if (timerRascunho.current) clearTimeout(timerRascunho.current);
    try { localStorage.removeItem(CHAVE_LOCAL); } catch { /* nada a fazer */ }
    try { await api.delete(`/api/pedidos/rascunho/${uuid}`); } catch { /* pode nem ter chegado la */ }

    setUuid(crypto.randomUUID());
    setItens([]);
    setCliente(null);
    setDescontoCabecalho(0);
    setObservacao('');
    setSimulacao(null);
    setRascunho({ estado: 'limpo', em: null });
  }

  async function confirmar() {
    if (salvando) return;                       // guard, além do disabled
    if (!cliente) {
      Swal.fire({ icon: 'info', title: 'Falta o cliente', text: 'Escolha o cliente antes de confirmar o orçamento.' });
      return;
    }
    if (!plano || !tipoDocumento) {
      Swal.fire({
        icon: 'info',
        title: !plano ? 'Falta a forma de pagamento' : 'Falta o tipo de documento',
        text: 'Escolha no bloco PAGAMENTO do orçamento. O Microuniverso não aceita o orçamento sem isso.',
        confirmButtonColor: '#EA6A08',
      });
      return;
    }
    if (simulacao?.precisa_liberacao) {
      // A liberação do gerente ainda não existe nesta versão (o backend recusa também).
      Swal.fire({
        icon: 'info',
        title: 'Desconto acima da alçada',
        text: 'A liberação do gerente ainda não existe no Fênix. Reduza o desconto para confirmar.',
        confirmButtonColor: '#EA6A08',
      });
      return;
    }
    const confirmacao = await Swal.fire({
      icon: 'question',
      title: 'Confirmar orçamento?',
      html: `Total <strong>${dinheiro(simulacao?.totais?.valor_liquido)}</strong> para <strong>${cliente.razao}</strong>.`
        + '<br/><span class="text-muted small">O orçamento vai agora para o Microuniverso, como <strong>Apenas orçando</strong>.</span>',
      showCancelButton: true,
      confirmButtonText: 'Confirmar',
      cancelButtonText: 'Voltar',
      confirmButtonColor: '#EA6A08',
    });
    if (!confirmacao.isConfirmed) return;

    // O Microuniverso leva mais de um minuto para criar o orçamento (medido em
    // 29/10/2026). Sem aviso, o vendedor clicaria de novo.
    Swal.fire({
      title: 'Enviando ao Microuniverso…',
      html: 'Pode levar até dois minutos. Não feche esta tela.',
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => Swal.showLoading(),
    });
    if (timerRascunho.current) clearTimeout(timerRascunho.current);   // o confirmar já grava o que está na tela
    setSalvando(true);
    try {
      const { data } = await api.post('/api/pedidos', {
        // O MESMO uuid do rascunho: confirmar é o rascunho virando pedido, não
        // um pedido novo. Também é a idempotência — retry de rede não duplica.
        uuid,
        filial_venda: filial,
        codclie: cliente.codclie,
        cliente_rascunho_id: cliente.codclie ? undefined : cliente.id,
        plano_pagamento: plano,
        tipo_documento: tipoDocumento || null,
        tpo,
        desconto_cabecalho: Number(descontoCabecalho) || 0,
        observacao,
        confirmar: true,
        origem: 'web',
        itens: itens.map((i) => ({
          codpro: i.codpro,
          quantidade: Number(i.quantidade) || 0,
          preco_unitario: Number(i.preco_unitario) || 0,
          desconto_unitario: Number(i.desconto_unitario) || 0,
          tipo_entrega: i.tipo_entrega || 'C',
          filial_retirada: i.filial_retirada || null,
          faturamento_direto: i.faturamento_direto || 0,
          dt_prev_ent: i.dt_prev_ent || null,
          sem_saldo_confirmado: !!i.sem_saldo_confirmado,
          observacao: i.observacao || null,
        })),
      });

      // Confirmado: o espelho local não serve mais e ofereceria retomar um
      // orçamento que já virou pedido.
      try { localStorage.removeItem(CHAVE_LOCAL); } catch { /* nada a fazer */ }

      await Swal.fire(data.status === 'INTEGRADO'
        ? {
          icon: 'success',
          title: `Orçamento ${data.numped_erp}`,
          html: `Está no Microuniverso com o número <strong>${data.numped_erp}</strong>, como Apenas orçando.`
            + `<br/><span class="text-muted small">No Fênix: ${data.numero}</span>`,
          confirmButtonColor: '#EA6A08',
        }
        : {
          // DIVERGENTE: o Microuniverso não respondeu e o orçamento não apareceu lá (ainda).
          icon: 'warning',
          title: `Orçamento ${data.numero} sem resposta`,
          text: data.erro || 'O Microuniverso não respondeu. Confira a lista de pedidos antes de refazer.',
          confirmButtonColor: '#EA6A08',
        });
      navigate('/pedidos');
    } catch (e) {
      if (e.response?.data?.exige_vendedor) {
        // O crachá venceu enquanto o orçamento era montado. Nada se perde: o
        // documento continua na tela, só é preciso reconfirmar quem está vendendo.
        setCracha(null);
        setVendedor(null);
        Swal.fire({
          icon: 'info',
          title: 'Confirme quem está vendendo',
          text: 'Passou do tempo desde que a senha do vendedor foi digitada. Informe de novo — o orçamento não se perdeu.',
          confirmButtonColor: '#EA6A08',
        });
      } else {
        Swal.fire({ icon: 'error', title: 'Não foi possível confirmar', text: mensagemDeErro(e) });
      }
    } finally {
      setSalvando(false);
    }
  }

  function aoIdentificarVendedor(v) {
    setVendedor(v);
    if (v.filial) setFilial(v.filial);   // começa na loja dele
    // Quem veio da tela de Rascunhos já escolheu qual retomar.
    const alvo = params.get('retomar');
    if (alvo) retomarAlvo(alvo, v); else oferecerRetomada(v);
  }

  // Só no dev: o crachá lembrado na aba entra direto (ver services/api.js).
  const restaurouDev = useRef(false);
  useEffect(() => {
    if (restaurouDev.current || vendedor) return;
    restaurouDev.current = true;
    const lembrado = crachaDevLembrado();
    if (lembrado) { setCracha(lembrado.cracha); aoIdentificarVendedor(lembrado.vendedor); }
  }, []);

  const totais = simulacao?.totais || {};
  const podeConfirmar = !!simulacao?.pode_confirmar && !!cliente && itens.length > 0 && !salvando;

  if (!vendedor) {
    return (
      <>
        <h1 className="h4 fw-bold mb-1">Novo orçamento</h1>
        <p className="text-muted small mb-4">
          O tipo de entrega é definido <strong>item a item</strong>, como no Microuniverso.
        </p>
        <IdentificaVendedor aoIdentificar={aoIdentificarVendedor} />
      </>
    );
  }

  return (
    <>
      <div className="nao-imprimir mb-3">
        <FaixaEstado estoque={estoque} />
      </div>

      {/* O vendedor precisa VER que está salvo. A garantia que ninguém enxerga
          não tranquiliza ninguém — e é justo o medo de perder o trabalho que
          faz alguém não usar a ferramenta. */}
      {temConteudo && (
        <div className={`selo-rascunho nao-imprimir selo-rascunho--${rascunho.estado}`}>
          {rascunho.estado === 'salvando' && (<><span className="spinner-border spinner-border-sm" />Salvando…</>)}
          {rascunho.estado === 'pendente' && (<><i className="bi bi-pencil" />Alterações não salvas…</>)}
          {rascunho.estado === 'salvo' && (
            <>
              <i className="bi bi-check-circle-fill" />
              Rascunho salvo{rascunho.numero ? ` como ${rascunho.numero}` : ''}
              {rascunho.em && ` às ${new Date(rascunho.em).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`}
            </>
          )}
          {rascunho.estado === 'offline' && (
            <>
              <i className="bi bi-cloud-slash" />
              Sem conexão com o servidor — o orçamento está guardado neste computador.
            </>
          )}
          {rascunho.estado !== 'offline' && (
            <button type="button" className="selo-rascunho__descartar" onClick={descartarRascunho}>
              descartar orçamento
            </button>
          )}
        </div>
      )}

      <div className="card">
        <div className="card-body p-0">
          <DocumentoOrcamento pedido={documento} itens={itensDocumento} previa edicao={edicao} />
        </div>
      </div>

      {/* A barra de fechamento vive fora do papel: ela é a ação, não o documento. */}
      <div className="barra-fechamento nao-imprimir">
        <div className="barra-fechamento__total">
          <span className="text-muted small">Total</span>
          <span className="h4 fw-bold mb-0" style={{ color: 'var(--primary)' }}>
            {simulando ? <span className="spinner-border spinner-border-sm" /> : dinheiro(totais.valor_liquido)}
          </span>
          {totais.markup_resultante != null && (
            <span className="text-muted" style={{ fontSize: '.76rem' }}>
              markup {(Number(totais.markup_resultante) * 100).toFixed(1)}%
              {simulacao?.alcada?.markup_abaixo_do_piso && (
                <span className="text-danger ms-1">— abaixo do piso</span>
              )}
            </span>
          )}
        </div>

        <div className="barra-fechamento__msgs">
          {/* Erro de item já aparece na linha dele; aqui ficam só os do pedido. */}
          {(simulacao?.erros || []).filter((e) => !e.startsWith('Item ')).map((e, i) => (
            <div key={i} className="erp-doc__msg erp-doc__msg--erro">
              <i className="bi bi-x-circle-fill" /><span>{e}</span>
            </div>
          ))}
          {itens.length > 0 && !cliente && (
            <div className="erp-doc__msg erp-doc__msg--erro">
              <i className="bi bi-x-circle-fill" /><span>Escolha o cliente no bloco CLIENTE.</span>
            </div>
          )}
          {simulacao?.precisa_liberacao && (
            <div className="erp-doc__msg erp-doc__msg--aviso">
              <i className="bi bi-shield-exclamation" />
              <span>Desconto acima da sua alçada — o orçamento vai para liberação do gerente.</span>
            </div>
          )}
        </div>

        <button className="btn btn-primary-fnx py-2 px-4 fw-semibold"
                disabled={!podeConfirmar} onClick={confirmar}>
          {salvando
            ? (<><span className="spinner-border spinner-border-sm me-2" />Confirmando…</>)
            : (<><i className="bi bi-check2-circle me-2" />Confirmar orçamento</>)}
        </button>
      </div>
    </>
  );
}
